import type {
  InputDescription,
  IntegrationDescription,
} from "@smartsuite/automation-engine-api";
import { ValueDescription_T } from "@smartsuite/automation-engine-api";

import { observer } from "mobx-react-lite";
import type { Ref } from "react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { CaptionBlock } from "../../../../../../../../components/CaptionBlock";
import { useTheme } from "../../../../../../../../stores";
import type {
  ActionCandidate,
  InputCandidate,
  TriggerCandidate,
} from "../../../../../../../../types/candidate";
import type {
  DynamicSection,
  DynamicSectionOption,
  DynamicSectionOptionGroup,
} from "../../../../../../../../types/line";
import { getLocalCssColorVariables } from "../../../../../../../../utils/cssVariablesUtils";
import {
  convertActionsToDynamicSections,
  convertTriggerToDynamicSection,
} from "../../../../utils";
import { StyledContainerWrapper } from "../../../style";
import { ActionOptions } from "../../containers/ActionOptions";
import { TriggerOptions } from "../../containers/TriggerOptions";
import {
  actionsToOptions,
  calculateConfigValue,
  ifLoopingSourceIsActionFind,
} from "../utils";
import { Root, StyledReactInputFieldContainer } from "./style";
import type { NumberPill, Option } from "./utils";
import {
  useConverters,
  ejectPillFromInput,
  mergeNextPillToInput,
  triggerToOptions,
} from "./utils";
import { POSITION_AT, POSITION_MY } from "../../../../style";
import { useRootStore } from "../../../../../../../../stores/hooks";
import { FieldMissingError } from "../../../../../../Sections/ErrorSection";
import { useFieldMissingValidation } from "../../../../../../../../stores/automation/hooks/field-missing-validation";
import { ActionLabel } from "../../../../../../../../utils/constants";
import { UpdateValueDropdown } from "../../containers/UpdateValueDropdown";
import { isMathOption } from "../../containers/UpdateValueDropdown/utils";
import type { UpdateValueType } from "../../containers/UpdateValueDropdown/components/UpdateValueOption";
import type { MathOptions } from "../../containers/UpdateValueDropdown/components/MathOption";
import { isTruthy } from "remeda";
import type { PillNodeAttrs } from "@smartsuite/smartdoc/lib/esm/extensions/pill/extension";
import {
  DropdownPanel,
  MenuOption,
  MenuOptionWrapper,
  PickerInputField,
  useSelectState,
} from "@smartsuite/react-ui/lib";
import { SolutionColorVariables } from "@smartsuite/types";
import { CurrentOption } from "../../containers/CurrentOption";
import { Tab } from "../../containers/TriggerDropdown/TriggerDropdown";
import { useLoopingMenu } from "../../../../hooks/use-looping-menu";
import { createLoopItemPill } from "../../../../utils/createLoopItemPill";
import { SubMenuOptions } from "../../containers/SubMenuOptions";
import {
  actionSourceBoundary,
  canOfferActionSources,
  resolveEditorLoopScope,
  opensTriggerFieldsDirectly,
  resolveCurrentItemScope,
} from "../../../../utils/action-sources";
import type { LoopScope } from "../../../../../../../../stores/automation/looping.store";

export const NumberEditor = observer<{
  config: InputCandidate;
  trigger: TriggerCandidate;
  actions: ActionCandidate[];
  action?: ActionCandidate;
  /** Set by ArgumentEditor when `actions` is an explicit, already-scoped list
   * (Conditional Logic branch conditions): offer it whole even though there is
   * no owning `action` to bound it (BUG-21770). */
  actionsArePreScoped?: boolean;
  /** The loop whose Current Item this editor may offer, used verbatim when
   * `actionsArePreScoped` is set. See useLoopingMenu (BUG-21770). */
  loopScope?: LoopScope;
  onChange: (input: InputCandidate) => void;
  integrations: IntegrationDescription[];
  allowNegative: boolean;
  onClickTrash?: () => void;
  isError?: boolean;
  description?: InputDescription;
  maxValue?: number;
}>(
  ({
    config,
    trigger,
    actions,
    action,
    actionsArePreScoped,
    loopScope,
    onChange,
    integrations,
    allowNegative,
    description,
    isError,
    onClickTrash,
    maxValue,
  }) => {
    const { t } = useTranslation();
    const store = useRootStore();
    const theme = useTheme();
    const [isOpen, setIsOpen] = useState(false);
    const [tab, setTab] = useState<
      | "root"
      | "trigger-fields"
      | "action-fields"
      | "options"
      | "current"
      | "sub-menu"
    >("root");
    const [triggerFields, setTriggerFields] = useState<Option[]>([]);
    const [actionsFields, setActionsFields] = useState<Option[]>([]);
    const [triggerMenu, setTriggerMenu] = useState<DynamicSection | null>(null);
    const [actionMenus, setActionMenus] = useState<DynamicSection[]>([]);
    const [selectedActionMenu, setSelectedActionMenu] = useState<
      DynamicSection | undefined
    >();
    type NumberEditorTab =
      | "root"
      | "trigger-fields"
      | "action-fields"
      | "options"
      | "current"
      | "sub-menu";
    const [subOptions, setSubOptions] = useState<DynamicSectionOptionGroup>({
      options: [],
      label: "",
    });
    const [prevTab, setPrevTab] = useState<NumberEditorTab>("root");
    const [focus, setFocus] = useSelectState();
    const buttonRef = useRef<HTMLButtonElement>();
    const [pills, setPills] = useState<NumberPill[]>([]);
    const [hasSingleMenuItem, setHasSingleMenuItem] = useState<boolean>(false);
    const { isInputValidByFieldMissing, getFieldMissingErrorMessage } =
      useFieldMissingValidation();
    const editorWrapperId = `${config.inputId}-editor-wrapper`;
    const [actionType, setActionType] = useState<
      | "replace"
      | "clear"
      | "append"
      | "mathAdd"
      | "mathSubtract"
      | "mathMultiply"
      | "mathDivide"
      | undefined
    >(config.operatorConfig?.operator?.oneofKind);
    const [isCleanAction, setIsCleanAction] = useState<boolean>(false);
    const rootLooping = store.automations.candidate.looping;
    const looping = resolveEditorLoopScope(
      action,
      rootLooping,
      loopScope,
      actionsArePreScoped,
    );

    const { convertInputToPills } = useConverters();

    const switchTab = (newTab: NumberEditorTab): void => {
      setPrevTab(tab);
      setTab(newTab);
    };

    const { loopingMenu, loopingFields } = useLoopingMenu({
      loopScope,
      actionsArePreScoped,
      trigger,
      actions,
      integrations,
      types: [ValueDescription_T.NUMBER],
      ssFieldTypes: [
        "numberfield",
        "autonumberfield",
        "currencyfield",
        "percentfield",
        "percentcompletefield",
        "numbersliderfield",
        "ratingfield",
        "countfield",
        "rollupfield",
        "votefield",
        "durationfield",
      ],
      action,
    });

    const offersCurrentItem =
      resolveCurrentItemScope(
        store.automations.candidate,
        loopScope,
        actionsArePreScoped,
      ) !== undefined;

    useEffect(() => {
      if (
        opensTriggerFieldsDirectly(actionMenus, triggerMenu, offersCurrentItem)
      ) {
        setTab("trigger-fields");
        setHasSingleMenuItem(true);
      } else {
        setTab("root");
        setHasSingleMenuItem(false);
      }
    }, [actionMenus, triggerMenu, offersCurrentItem]);

    useEffect(() => {
      setTriggerMenu(
        convertTriggerToDynamicSection(
          trigger,
          integrations,
          [ValueDescription_T.NUMBER, ValueDescription_T.LIST],
          isMathOption(actionType as UpdateValueType | MathOptions)
            ? [ValueDescription_T.NUMBER]
            : [ValueDescription_T.NUMBER, ValueDescription_T.STRING],
          [
            "numberfield",
            "autonumberfield",
            "currencyfield",
            "percentfield",
            "percentcompletefield",
            "numbersliderfield",
            "ratingfield",
            "countfield",
            "rollupfield",
            "votefield",
            "textfield",
            "durationfield",
          ],
          [""],
        ),
      );
      setTriggerFields(triggerToOptions(trigger));
    }, [integrations, trigger, actionType]);

    useEffect(() => {
      if (canOfferActionSources(action, actionsArePreScoped)) {
        setActionsFields(
          actionsToOptions(
            actions,
            [ValueDescription_T.NUMBER, ValueDescription_T.STRING],
            [
              "numberfield",
              "autonumberfield",
              "currencyfield",
              "percentfield",
              "percentcompletefield",
              "numbersliderfield",
              "ratingfield",
              "countfield",
              "rollupfield",
              "votefield",
              "durationfield",
              "textfield",
            ],
          ),
        );
        setActionMenus(
          convertActionsToDynamicSections(
            actions,
            actionSourceBoundary(action, actionsArePreScoped),
            integrations,
            [ValueDescription_T.NUMBER, ValueDescription_T.STRING],
            [
              "numberfield",
              "autonumberfield",
              "currencyfield",
              "percentfield",
              "percentcompletefield",
              "numbersliderfield",
              "ratingfield",
              "countfield",
              "rollupfield",
              "votefield",
              "durationfield",
              "textfield",
            ],
            [],
            looping,
          ),
        );
      }
    }, [actions, action, integrations, actionsArePreScoped, loopScope]);

    useEffect(() => {
      setPills(
        convertInputToPills(
          trigger,
          config,
          triggerFields,
          actionsFields,
          actions,
          action,
          loopingFields,
        ),
      );
    }, [actionsFields, config, triggerFields]);

    const addPill = (attrs: NumberPill | PillNodeAttrs): void => {
      setTimeout(() => {
        setIsOpen(false);
      });
      const newPills = [attrs];
      onChange({
        ...config,
        value: calculateConfigValue(newPills, actions, trigger),
      });
    };

    useEffect(() => {
      if (store.inputToFocus?.inputId === config.inputId) {
        setIsOpen(!!isMenuAvaliable);
        const focusTimeoutId = setTimeout(() => {
          const wrapper = document.getElementById(editorWrapperId);
          const editor = wrapper?.querySelector(".picker-input-field__input");
          // @ts-expect-error
          editor?.focus();
          store.inputToFocus = undefined;
        });
        return () => clearTimeout(focusTimeoutId);
      }
    }, []);

    useEffect(() => {
      if (actionType === undefined) return;

      if (actionType === "clear") {
        setIsOpen(false);
        setIsCleanAction(true);
      } else {
        setIsCleanAction(false);
      }

      if (isMathOption(actionType as UpdateValueType | MathOptions)) {
        const newPills = pills.filter((pill) => {
          if (pill.type === "triggerOutput") {
            const isOptionExist = triggerMenu?.options.find(
              (option) => option.outputId === pill.value,
            );
            return isTruthy(isOptionExist);
          }
          return true;
        });
        if (newPills.length !== pills.length) {
          onChange({
            ...config,
            value: calculateConfigValue(newPills, actions, trigger),
            operatorConfig: {
              // @ts-expect-error
              operator: {
                oneofKind: actionType,
                [actionType]: {},
              },
            },
          });
          return;
        }
      }

      onChange({
        ...config,
        value:
          actionType === "clear"
            ? {
                values: [],
              }
            : config.value,
        operatorConfig: {
          // @ts-expect-error
          operator: {
            oneofKind: actionType,
            [actionType]: {},
          },
        },
      });
    }, [actionType, triggerMenu]);

    const isNextPillAllowed = (config: InputCandidate): boolean => {
      if (config.value?.values.length === 1) {
        if (focus) {
          setFocus(false);
        }
        return false;
      } else {
        return true;
      }
    };

    const changeActionType = (
      type:
        | "replace"
        | "clear"
        | "append"
        | "mathAdd"
        | "mathSubtract"
        | "mathMultiply"
        | "mathDivide"
        | undefined,
    ): void => {
      setActionType(type);
    };

    const triggerOutputs = triggerMenu?.options ?? [];
    const loopingMenuOptions = loopingMenu?.options ?? [];
    const isMenuAvaliable =
      triggerOutputs.length > 0 ||
      loopingMenuOptions.length > 0 ||
      actionMenus.some((menu) => menu.options.length > 0);

    const goSubMenu = (
      options: DynamicSectionOption[],
      label: string,
    ): void => {
      setSubOptions({ options, label });
      switchTab("sub-menu");
    };

    const getPrevTab = (currentTab: NumberEditorTab): Tab => {
      if (currentTab === "trigger-fields") {
        return Tab.TRIGGER_FIELDS;
      }
      if (currentTab === "action-fields") {
        return Tab.ACTION_FIELDS;
      }
      if (currentTab === "current") {
        return Tab.CURRENT_ITEM;
      }
      return Tab.ROOT;
    };

    const disabledOptionId = pills.map((pill) => pill.title);

    return (
      <StyledContainerWrapper>
        {description !== undefined && (
          <CaptionBlock description={description} onClickTrash={onClickTrash} />
        )}
        <Root>
          <StyledReactInputFieldContainer id={editorWrapperId}>
            <PickerInputField
              showAddButton={!isCleanAction}
              className={`${
                (isError ?? !isInputValidByFieldMissing(config))
                  ? "is-error"
                  : ""
              } ${isCleanAction ? "is-clear" : ""}`}
              state={
                (isError ?? !isInputValidByFieldMissing(config))
                  ? "error"
                  : isOpen || focus
                    ? "active"
                    : "default"
              }
              onChangeFocused={(isFocused, event) => {
                setFocus(isFocused);

                if (
                  isFocused &&
                  isMenuAvaliable &&
                  event?.target.className !== "field-type-pill__remove"
                ) {
                  setIsOpen(true);
                }
              }}
              onInputChange={() => {
                setIsOpen(false);
              }}
              pickerIcon={isMenuAvaliable ? undefined : ""}
              pattern={
                allowNegative
                  ? /^[-]*[0-9]*([0-9]+[,.][0-9]*)?$/
                  : /^[0-9]*([0-9]+[,.][0-9]*)?$/
              }
              // @ts-expect-error
              buttonRef={buttonRef as Ref<HTMLButtonElement>}
              isOpen={isOpen}
              pills={pills}
              pillStyle={{
                textSize: "m",
              }}
              onPillRemove={(index: number) => {
                setIsOpen(false);
                onChange(ejectPillFromInput(config, index));
              }}
              allowManualInput={!isCleanAction && isNextPillAllowed(config)}
              onInputAdd={(number: string) => {
                setIsOpen(false);
                if (maxValue !== undefined && Number(number) > maxValue) {
                  return;
                }
                onChange(
                  mergeNextPillToInput(config, {
                    type: "custom",
                    number,
                  }),
                );
              }}
              placeholder={
                isCleanAction
                  ? t("automation-form.clean-operator-editor-placeholder")
                  : focus
                    ? t("automation-form.number-editor-placeholder")
                    : ""
              }
            >
              {action?.actionReference?.actionId === ActionLabel.updateRecord &&
                description?.operators?.operators !== undefined &&
                description?.operators?.operators.length > 0 && (
                  <UpdateValueDropdown
                    operatorConfig={config.operatorConfig}
                    operatorDescriptions={description?.operators.operators}
                    onOpenedChange={setFocus}
                    onChange={changeActionType}
                  />
                )}
              {isOpen && (
                <DropdownPanel
                  targetElementRef={buttonRef}
                  isActive={isOpen}
                  at={POSITION_AT}
                  my={POSITION_MY}
                  minWidth={300}
                  arrowBackground="custom"
                  arrowCustomBackground={SolutionColorVariables.sc1}
                  panelLineBackground={SolutionColorVariables.sc1}
                  portalStyles={getLocalCssColorVariables(theme)}
                  onClickOutside={() => setIsOpen(false)}
                  onCloseAnimationEnd={() => setIsOpen(false)}
                >
                  {tab === "root" && (
                    <MenuOptionWrapper>
                      <MenuOption
                        submenuArrow="right"
                        onClick={() => {
                          switchTab("trigger-fields");
                        }}
                      >
                        {t("field-popups.trigger-popup")}
                      </MenuOption>
                      {actionMenus.map(
                        (actionMenu) =>
                          actionMenu?.options.length > 0 && (
                            <MenuOption
                              key={actionMenu.hash}
                              submenuArrow="right"
                              onClick={() => {
                                setSelectedActionMenu(actionMenu);
                                switchTab("action-fields");
                              }}
                            >
                              {actionMenu.hideNamePrefix === true
                                ? actionMenu.name
                                : t("field-popups.action-popup", {
                                    name: actionMenu.name,
                                  })}
                            </MenuOption>
                          ),
                      )}

                      <CurrentOption
                        loopingMenu={loopingMenu}
                        loopScope={looping}
                        actionsArePreScoped={actionsArePreScoped}
                        addPill={(looping) => {
                          // Case when source for looping is the find action
                          if (
                            ifLoopingSourceIsActionFind(looping) ||
                            loopingMenu?.options?.length
                          ) {
                            switchTab("current");
                            return;
                          }

                          if (looping.source) {
                            const loopPill = createLoopItemPill(looping);
                            if (loopPill) {
                              addPill(loopPill);
                            }
                          }
                        }}
                      />
                    </MenuOptionWrapper>
                  )}

                  {tab === "trigger-fields" && (
                    <TriggerOptions
                      searchPlaceholder={t("automation-form.search-fields")}
                      title={t("automation-form.fields-from-trigger")}
                      onNavigateBack={() =>
                        !hasSingleMenuItem && switchTab("root")
                      }
                      addPill={addPill}
                      options={triggerOutputs}
                      disabledOptionId={disabledOptionId}
                      hasSingleMenuItem={hasSingleMenuItem}
                      goSubMenu={goSubMenu}
                    />
                  )}
                  {tab === "sub-menu" && (
                    <SubMenuOptions
                      title={subOptions.label}
                      hasSingleMenuItem={
                        triggerOutputs.length === 0 &&
                        actionMenus.length === 0 &&
                        loopingMenu?.options.length === 0
                      }
                      onNavigateBack={() => setTab(prevTab)}
                      addPill={addPill}
                      options={subOptions.options}
                      selectedActionHash={selectedActionMenu?.hash}
                      loopingMenuHash={loopingMenu?.hash}
                      prevTab={getPrevTab(prevTab)}
                      disabledOptionId={disabledOptionId}
                    />
                  )}
                  {tab === "current" && loopingMenu && (
                    <TriggerOptions
                      tab={Tab.LOOPING_FIELDS}
                      searchPlaceholder={t("automation-form.search-fields")}
                      title={loopingMenu.name}
                      onNavigateBack={() => switchTab("root")}
                      addPill={addPill}
                      options={loopingMenu.options}
                      hasSingleMenuItem={false}
                      loopingMenuHash={loopingMenu.hash}
                      disabledOptionId={disabledOptionId}
                      loopingMenu={loopingMenu}
                      goSubMenu={goSubMenu}
                    />
                  )}
                  {tab === "action-fields" && (
                    <ActionOptions
                      onNavigateBack={() => switchTab("root")}
                      addPill={addPill}
                      selectedActionMenu={selectedActionMenu}
                      selectedActionHash={selectedActionMenu?.hash}
                      goSubMenu={goSubMenu}
                      disabledOptionId={disabledOptionId}
                    />
                  )}
                </DropdownPanel>
              )}
            </PickerInputField>
            <FieldMissingError
              message={getFieldMissingErrorMessage(config, action)}
            />
          </StyledReactInputFieldContainer>
        </Root>
      </StyledContainerWrapper>
    );
  },
);
